import * as React from 'react';

import styled from '@emotion/styled';
import { Badge } from '@lightspeed/flame/Badge';
import { Bone } from '@lightspeed/flame/Bone';
import { Box } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';
import { Heading2, Heading4, Text, TextLink } from '@lightspeed/flame/Text';

import AlertMessageDisplay from 'root/apps/components/AlertMessageDisplay/AlertMessageDisplay';
import useLocalizedLink from 'root/apps/hooks/useLocalizedLink';
import { SendToNuorderButton } from 'root/apps/PurchaseOrder/NuorderOrdering/components/SendToNuorderButton';
import { CustomTextLink } from 'root/apps/PurchaseOrder/NuorderOrdering/components/subcomponents/CustomTextLink';
import {
  NuorderOrderingActionTypes,
  useNuorderOrdering,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Context';

const Heading4Styled = styled(Heading4)`
  white-space: nowrap;
`;

export const PageHeader: React.FC = () => {
  const NUORDER_HELP_CENTRE_URL =
    'https://retail-support.lightspeedhq.com/hc/{{locale}}/articles/38633631918363-Placing-integrated-purchase-orders-with-Retail-POS-R-Series-and-NuORDER';

  const localizedHelpCentreUrl = useLocalizedLink(NUORDER_HELP_CENTRE_URL);

  const {
    state: { purchase, vendor, alertMessage, nuorderOrder, configs, isFetchingOrder },
    dispatch,
  } = useNuorderOrdering();

  const handleAlertMessageClose = () => {
    dispatch({
      type: NuorderOrderingActionTypes.SET_ALERT_MESSAGE,
      payload: null,
    });
  };

  const status = React.useMemo(() => {
    if (isFetchingOrder) return 'loading';
    if (!purchase?.b2bOrderUID) return 'open';
    if (!nuorderOrder) return 'n/a';
    return nuorderOrder.status;
  }, [isFetchingOrder, nuorderOrder, purchase]);

  const statusColor = React.useMemo(() => {
    switch (status) {
      case 'open':
        return 'gray-100';
      case 'n/a':
      case 'draft':
        return 'orange-200';
      case 'approved':
      case 'shipped':
      case 'processed':
        return 'green-100';
      case 'archived':
      case 'cancelled':
        return 'maple-200';
      case 'edited':
      case 'pending':
      case 'review':
      case 'working':
      default:
        return 'blue-200';
    }
  }, [status]);

  const viewOnNuorderLink = React.useMemo(() => {
    if (isFetchingOrder) return '';
    if (!purchase?.b2bOrderUID) return '';
    if (!vendor?.b2bSellerUID) return '';
    if (!configs?.b2bWebServiceBaseURI) return '';
    if (!nuorderOrder)
      return `${configs.b2bWebServiceBaseURI}/brand/${vendor.b2bSellerUID}/orders?__bulk_search_type=po_number&__bulk_search=${purchase.orderID}`;
    return `${configs.b2bWebServiceBaseURI}/brand/${vendor.b2bSellerUID}/order/${purchase.b2bOrderUID}`;
  }, [purchase, vendor, configs, isFetchingOrder, nuorderOrder]);

  const isLoading = React.useMemo(() => {
    return !purchase || !vendor || !configs;
  }, [purchase, vendor, configs]);

  return (
    <Box>
      {alertMessage && (
        <AlertMessageDisplay
          alertMessage={{
            message: alertMessage.message,
            type: alertMessage.type,
            title: alertMessage.title,
          }}
          onClose={handleAlertMessageClose}
          truncateMessage={false}
          mb={2}
        />
      )}
      {isLoading ? (
        <Box display="flex" justifyContent="space-between" data-test="bones">
          <Box>
            <Heading2>
              <Bone height="1.5em" width="20em" />
            </Heading2>
            <Text mt={1}>
              <Bone height="1em" width="14em" />
            </Text>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Bone height="8em" width="10em" />
          </Box>
        </Box>
      ) : (
        <Box display="flex" justifyContent="space-between">
          <Box flexBasis="70%">
            <Heading2>
              Order #<var data-test="purchaseID">{purchase?.orderID}</var> to{' '}
              <var data-test="vendorName">{vendor?.name}</var>
            </Heading2>
            <p>
              You can send your PO to NuORDER to create and submit a working order to the vendor.
              Once the purchase order is sent, any changes must be made through NuORDER.{' '}
              <TextLink fontWeight="regular" href={localizedHelpCentreUrl} target="_blank">
                Learn more <IconExternalLink color={theme.textStyles.link.color} />
              </TextLink>
            </p>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <SendToNuorderButton />
            <Box display="flex" mt={1} textAlign="right">
              <Box>
                <Heading4Styled mb={2}>Status</Heading4Styled>
                {status === 'loading' ? (
                  <Bone width={60} height={25} data-test="orderStatus-bones" />
                ) : (
                  <Badge data-test="orderStatus" bg={statusColor} data-test-color={statusColor}>
                    {status}
                  </Badge>
                )}
                {viewOnNuorderLink && (
                  <Box mt={2}>
                    <CustomTextLink
                      href={viewOnNuorderLink}
                      text="View on NuORDER"
                      blank={true}
                      data-test="viewOnNuorderLink"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
